import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, FileText, FilePdf, FileHtml, Package, Download, Eye,
  Sparkle, CheckCircle, Warning, ArrowRight, Copy,
  Lightning, Briefcase, Users, Money, ShoppingCart, Handshake,
  Image as ImageIcon, Archive
} from '@phosphor-icons/react';
import api from '@/utils/api';
import { toast } from 'sonner';
import BusinessMemoryBanner from './BusinessMemoryBanner';

// Business type icons
const BUSINESS_TYPE_ICONS = {
  service: Briefcase,
  digital_product: ShoppingCart,
  coaching: Users,
  affiliate: Handshake,
  hybrid: Lightning
};

// File type configurations
const FILE_TYPE_CONFIG = {
  pdf: { icon: FilePdf, color: 'text-red-400', bgColor: 'bg-red-500/20', label: 'PDF' },
  text: { icon: FileText, color: 'text-slate-400', bgColor: 'bg-slate-500/20', label: 'TXT' },
  html_template: { icon: FileHtml, color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'HTML' },
  assembled: { icon: FileHtml, color: 'text-green-400', bgColor: 'bg-green-500/20', label: 'FINAL' },
  image: { icon: ImageIcon, color: 'text-emerald-400', bgColor: 'bg-emerald-500/20', label: 'IMG' }
};

// Lazy loading thumbnail component
const ThumbnailImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-slate-700 animate-pulse rounded-lg" />
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-cover rounded-lg transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {error && (
        <div className="absolute inset-0 bg-slate-700 rounded-lg flex items-center justify-center">
          <FileText size={32} className="text-slate-500" />
        </div>
      )}
    </div>
  );
};

// Individual asset output card component
const OutputCard = ({ output, onDownload, onPreview, onCopy }) => {
  const config = FILE_TYPE_CONFIG[output.type] || FILE_TYPE_CONFIG.text;
  const Icon = config.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600 transition-all group"
      data-testid={`output-card-${output.filename}`}
    >
      {/* Thumbnail */}
      <div className="aspect-square relative overflow-hidden bg-slate-900">
        {output.thumbnail_url ? (
          <ThumbnailImage 
            src={output.thumbnail_url} 
            alt={output.filename}
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
            <Icon size={48} className={config.color} weight="duotone" />
          </div>
        )}
        
        {/* File type badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          {config.label}
        </div>
        
        {/* Editable badge */}
        {output.editable && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
            Editable
          </div>
        )}
        
        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onPreview?.(output)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title="Preview"
          >
            <Eye size={20} className="text-white" />
          </button>
          <button
            onClick={() => onDownload?.(output)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title="Download"
          >
            <Download size={20} className="text-white" />
          </button>
          {output.editable && (
            <button
              onClick={() => onCopy?.(output)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Copy"
            >
              <Copy size={20} className="text-white" />
            </button>
          )}
        </div>
      </div>
      
      {/* Card info */}
      <div className="p-3">
        <p className="text-white text-sm font-medium truncate" title={output.filename}>
          {output.filename}
        </p>
        <p className="text-slate-400 text-xs mt-1 truncate" title={output.description}>
          {output.description}
        </p>
      </div>
    </motion.div>
  );
};

// Asset group card component
const AssetGroupCard = ({ asset, onDownload, onPreview, onCopy, planTier }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Determine which outputs to show based on tier
  const filterOutputsByTier = (outputs) => {
    if (planTier === 'pro') return outputs;
    if (planTier === 'growth') return outputs.filter(o => o.type !== 'assembled');
    return outputs.filter(o => ['pdf', 'text'].includes(o.type));
  };
  
  const visibleOutputs = filterOutputsByTier(asset.outputs || []);
  
  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Asset header with main thumbnail */}
      <div 
        className="p-4 cursor-pointer hover:bg-slate-800/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid={`asset-group-${asset.key}`}
      >
        <div className="flex items-center gap-4">
          {/* Main thumbnail */}
          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
            {asset.thumbnail_url ? (
              <ThumbnailImage 
                src={asset.thumbnail_url} 
                alt={asset.name}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                <FileText size={24} className="text-slate-400" />
              </div>
            )}
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-medium">{asset.name}</h3>
            <p className="text-slate-400 text-sm truncate">{asset.description}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-500">
                {visibleOutputs.length} file{visibleOutputs.length !== 1 ? 's' : ''}
              </span>
              {asset.included_in_zip && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full">
                  In ZIP
                </span>
              )}
            </div>
          </div>
          
          {/* Expand indicator */}
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            className="text-slate-400"
          >
            <ArrowRight size={20} className="rotate-90" />
          </motion.div>
        </div>
      </div>
      
      {/* Expanded outputs grid */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-700/50"
          >
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {visibleOutputs.map((output, idx) => (
                  <OutputCard
                    key={`${output.filename}-${idx}`}
                    output={output}
                    onDownload={onDownload}
                    onPreview={onPreview}
                    onCopy={onCopy}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PackagePreviewModal = ({ isOpen, onClose, onGenerate, user }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
    }
  }, [isOpen]);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const response = await api.get('/business-package/preview');
      setPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error(error.response?.data?.detail || 'Failed to load package preview');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/business-package/generate');

      if (response.data.success) {
        toast.success(`Package created with ${response.data.file_count} files! Saved to My Creations.`);
        onGenerate?.(response.data);
        onClose();
      }
    } catch (error) {
      console.error('Failed to generate package:', error);
      // Memory-guard error (backend returns 400 with structured detail)
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.missing_memory_fields) {
        toast.error(
          detail.message || "Let's finish setting up your business before we continue.",
          { duration: 7000 },
        );
      } else {
        toast.error(
          (typeof detail === 'string' ? detail : detail?.message) || 'Failed to generate package',
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadOutput = (output) => {
    toast.info(`Downloading ${output.filename}...`);
    // In a full implementation, this would trigger individual file download
  };

  const handlePreviewOutput = (output) => {
    toast.info(`Previewing ${output.filename}...`);
    // In a full implementation, this would open a preview modal
  };

  const handleCopyOutput = (output) => {
    toast.success(`Copied ${output.filename} content`);
    // In a full implementation, this would copy content to clipboard
  };

  if (!isOpen) return null;

  const BusinessTypeIcon = preview?.business_type ? BUSINESS_TYPE_ICONS[preview.business_type] || Briefcase : Briefcase;
  
  // Calculate total files across all assets
  const totalFiles = preview?.assets?.reduce((sum, asset) => sum + (asset.outputs?.length || 0), 0) || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="package-preview-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Package size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Business Package Preview</h2>
                  <p className="text-white/80 text-sm">Visual preview of your generated assets</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="close-preview-modal"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
            <div className="mb-4">
              <BusinessMemoryBanner />
            </div>            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 border-3 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mb-4" />
                <p className="text-slate-400">Loading preview...</p>
              </div>
            ) : !preview?.success ? (
              <div className="text-center py-12">
                <Warning size={48} className="text-amber-500 mx-auto mb-4" />
                <p className="text-white font-medium mb-2">Cannot Generate Package</p>
                <p className="text-slate-400 text-sm">{preview?.error || 'Please complete Business Builder first.'}</p>
              </div>
            ) : (
              <>
                {/* Business Info Card */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <BusinessTypeIcon size={24} className="text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-white font-medium">{preview.business_type_name}</span>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-xs rounded-full uppercase">
                          {preview.plan_tier}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm truncate">{preview.business_idea}</p>
                      <p className="text-slate-500 text-xs mt-1">For: {preview.audience}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-2xl font-bold text-white">{totalFiles}</div>
                      <div className="text-xs text-slate-400">Total Files</div>
                    </div>
                  </div>
                </div>

                {/* Integration Status */}
                {preview.integrations && (
                  <div className="flex items-center gap-4 mb-6 text-sm">
                    <div className="flex items-center gap-2">
                      {preview.integrations.pexels?.configured ? (
                        <CheckCircle size={16} className="text-green-400" weight="fill" />
                      ) : (
                        <Warning size={16} className="text-amber-400" />
                      )}
                      <span className="text-slate-400">
                        Pexels Images: {preview.integrations.pexels?.configured ? 'Active' : 'Not configured'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Assets Grid */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Archive size={18} className="text-purple-400" />
                      Package Contents
                    </h3>
                    <span className="text-slate-400 text-sm">{preview.assets?.length || 0} asset groups</span>
                  </div>
                  
                  {/* Asset Groups */}
                  <div className="space-y-3">
                    {preview.assets?.map((asset, index) => (
                      <AssetGroupCard
                        key={asset.key}
                        asset={asset}
                        planTier={preview.plan_tier}
                        onDownload={handleDownloadOutput}
                        onPreview={handlePreviewOutput}
                        onCopy={handleCopyOutput}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {preview?.success && (
            <div className="border-t border-slate-700 p-4 bg-slate-800/50">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="text-slate-400 text-sm flex items-center gap-2">
                  <Sparkle size={16} className="text-purple-400" />
                  <span>
                    {preview.plan_tier === 'pro' 
                      ? 'All files will be bundled in BusinessPackage.zip'
                      : `Upgrade to Pro for full package download`
                    }
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
                    data-testid="cancel-preview-btn"
                  >
                    Cancel
                  </button>
                  {preview.plan_tier === 'pro' && (
                    <button
                      onClick={handleGenerate}
                      disabled={generating}
                      data-testid="generate-package-btn"
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white font-medium rounded-lg flex items-center gap-2 transition-all shadow-lg shadow-purple-500/25"
                    >
                      {generating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Download size={18} weight="bold" />
                          <span>Generate & Download ZIP</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PackagePreviewModal;
